import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  encodeTabletProvisioning,
  hasRole,
  type Tablet,
  type TabletApprovalResponse,
} from '@collector/shared'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'
import { formatDateTime } from '../lib/format'
import { PageHeading } from '../components/PageHeading'
import { QrCanvas } from '../components/QrCanvas'

export function TabletsPage() {
  const { t } = useTranslation()
  const auth = useAuth()
  const isAdmin = hasRole(auth.user, 'admin', 'developer')
  const [params, setParams] = useSearchParams()
  const status = (params.get('status') ?? '') as '' | 'pending' | 'approved'
  const tablets = useQuery({
    queryKey: ['tablets', status],
    queryFn: () => api().tablets.list(status === '' ? undefined : status),
  })
  const queryClient = useQueryClient()
  const [issued, setIssued] = useState<{ tabletUid: string; secret: string } | null>(null)

  const approve = useMutation({
    mutationFn: (id: string) => api().tablets.approve(id),
    onSuccess: (resp: TabletApprovalResponse) => {
      setIssued({ tabletUid: resp.tablet.tabletUid, secret: resp.secret })
      void queryClient.invalidateQueries({ queryKey: ['tablets'] })
    },
  })

  const [revokeTarget, setRevokeTarget] = useState<Tablet | null>(null)
  const revoke = useMutation({
    mutationFn: (id: string) => api().tablets.revoke(id),
    onSuccess: () => {
      setRevokeTarget(null)
      void queryClient.invalidateQueries({ queryKey: ['tablets'] })
    },
  })

  return (
    <div>
      <PageHeading
        title={t('tablets.title')}
        description={t('tablets.description')}
        actions={
          <select
            className="input max-w-[12rem]"
            value={status}
            onChange={(e) => {
              const v = e.target.value
              setParams(v ? { status: v } : {})
            }}
          >
            <option value="">{t('common.all')}</option>
            <option value="pending">{t('tablets.filterPending')}</option>
            <option value="approved">{t('tablets.filterApproved')}</option>
          </select>
        }
      />

      {issued ? (
        <ApprovalDialog
          issued={issued}
          onDismiss={() => {
            setIssued(null)
          }}
        />
      ) : null}

      {revokeTarget ? (
        <ConfirmRevokeDialog
          tablet={revokeTarget}
          inFlight={revoke.isPending}
          onCancel={() => setRevokeTarget(null)}
          onConfirm={() => revoke.mutate(revokeTarget.id)}
        />
      ) : null}

      {tablets.isLoading ? (
        <p className="text-sm text-stone-500 dark:text-stone-400">{t('common.loading')}</p>
      ) : tablets.data && tablets.data.length > 0 ? (
        <div className="overflow-x-auto rounded-md border border-stone-200 bg-white dark:border-stone-700 dark:bg-stone-900">
          <table className="min-w-full divide-y divide-stone-200 text-sm dark:divide-stone-700">
            <thead>
              <tr className="table-header">
                <th className="px-3 py-2">{t('tablets.uid')}</th>
                <th className="px-3 py-2">{t('tablets.name')}</th>
                <th className="px-3 py-2">{t('tablets.status')}</th>
                <th className="px-3 py-2">{t('tablets.requested')}</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100 dark:divide-stone-800">
              {tablets.data.map((t) => (
                <TabletRow
                  key={t.id}
                  tablet={t}
                  isAdmin={isAdmin}
                  onApprove={() => approve.mutate(t.id)}
                  onRevoke={() => setRevokeTarget(t)}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-stone-500 dark:text-stone-400">{t('tablets.noneMatch')}</p>
      )}
    </div>
  )
}

interface RowProps {
  tablet: Tablet
  isAdmin: boolean
  onApprove: () => void
  onRevoke: () => void
}

function TabletRow({ tablet, isAdmin, onApprove, onRevoke }: RowProps) {
  const { t } = useTranslation()
  return (
    <tr>
      <td className="px-3 py-2 font-mono text-xs">{tablet.tabletUid}</td>
      <td className="px-3 py-2">{tablet.name}</td>
      <td className="px-3 py-2">
        <span
          className={`inline-flex rounded px-1.5 py-0.5 text-xs font-medium ${
            tablet.status === 'approved'
              ? 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200'
              : tablet.status === 'pending'
                ? 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200'
                : 'bg-stone-200 text-stone-700 dark:bg-stone-700 dark:text-stone-200'
          }`}
        >
          {tablet.status}
        </span>
      </td>
      <td className="px-3 py-2 text-stone-600 dark:text-stone-400">
        {formatDateTime(tablet.requestedAt)}
      </td>
      <td className="px-3 py-2 text-right">
        {isAdmin && tablet.status === 'pending' ? (
          <button className="btn-primary" onClick={onApprove}>
            {t('tablets.approve')}
          </button>
        ) : null}
        {isAdmin && tablet.status === 'approved' ? (
          <button className="btn-danger" onClick={onRevoke}>
            {t('tablets.revoke')}
          </button>
        ) : null}
      </td>
    </tr>
  )
}

interface ApprovalDialogProps {
  issued: { tabletUid: string; secret: string }
  onDismiss: () => void
}

/**
 * Modal shown once after a tablet is approved. Surfaces the freshly issued
 * provisioning payload (tablet UID + HMAC secret + API base URL) as a QR
 * code for camera-based transfer, with a Copy button as a clipboard fallback
 * for tablets that can't scan.
 *
 * The secret is only retrievable here — dismissing the dialog (or losing it
 * on a refresh) means the admin has to revoke and re-approve the tablet.
 */
function ApprovalDialog({ issued, onDismiss }: ApprovalDialogProps) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const provisioningJson = useMemo(
    () =>
      encodeTabletProvisioning({
        tabletUid: issued.tabletUid,
        secret: issued.secret,
        apiBaseUrl: `${window.location.origin}/api`,
      }),
    [issued.tabletUid, issued.secret],
  )

  const copySecret = async () => {
    // The button copies just the raw secret (not the full provisioning
    // payload) because its real purpose is dev testing — pasting into
    // `tablet-sim --secret …` or similar. Operators in the field should use
    // the QR instead.
    try {
      await navigator.clipboard.writeText(issued.secret)
      setCopied(true)
      setTimeout(() => {
        setCopied(false)
      }, 2000)
    } catch (err) {
      // Clipboard may be blocked (no https, permission denied, old browser).
      // Log it — silently failing would leave the admin thinking the copy worked.
      console.warn('clipboard write failed', err)
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="approval-dialog-title"
      className="fixed inset-0 z-10 flex items-center justify-center bg-black/40 px-4"
    >
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-stone-900">
        <h2
          id="approval-dialog-title"
          className="text-lg font-semibold text-stone-900 dark:text-stone-100"
        >
          {t('tablets.approvedTitle')}
        </h2>
        <p className="mt-1 text-sm text-stone-600 dark:text-stone-400">
          {t('tablets.approvedBody', { tabletUid: issued.tabletUid })}
        </p>

        <div className="mt-6 flex flex-col items-center gap-3">
          <div className="rounded border border-stone-200 bg-white p-2 dark:border-stone-700 dark:bg-stone-100">
            <QrCanvas
              value={provisioningJson}
              size={240}
              errorCorrectionLevel="M"
              ariaLabel={`Provisioning QR for tablet ${issued.tabletUid}`}
            />
          </div>
          <button
            className="btn-secondary"
            onClick={() => {
              void copySecret()
            }}
            title="Copy the raw secret (for dev/test use)"
          >
            {copied ? t('tablets.copied') : t('tablets.copySecret')}
          </button>
        </div>

        <div className="mt-6 flex justify-end">
          <button className="btn-primary" onClick={onDismiss}>
            {t('common.dismiss')}
          </button>
        </div>
      </div>
    </div>
  )
}

interface ConfirmRevokeDialogProps {
  tablet: Tablet
  inFlight: boolean
  onCancel: () => void
  onConfirm: () => void
}

function ConfirmRevokeDialog({ tablet, inFlight, onCancel, onConfirm }: ConfirmRevokeDialogProps) {
  const { t } = useTranslation()
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="revoke-tablet-title"
      className="fixed inset-0 z-10 flex items-center justify-center bg-black/40 px-4"
    >
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-stone-900">
        <h2
          id="revoke-tablet-title"
          className="text-lg font-semibold text-stone-900 dark:text-stone-100"
        >
          {t('tablets.confirmRevokeTitle')}
        </h2>
        <p className="mt-2 text-sm text-stone-600 dark:text-stone-400">
          {t('tablets.confirmRevokeBody', { name: tablet.name })}
        </p>
        <div className="mt-6 flex justify-end gap-2">
          <button className="btn-secondary" onClick={onCancel} disabled={inFlight}>
            {t('common.cancel')}
          </button>
          <button className="btn-danger" onClick={onConfirm} disabled={inFlight}>
            {inFlight ? t('tablets.revoking') : t('tablets.revoke')}
          </button>
        </div>
      </div>
    </div>
  )
}
